/**
 * Modificateurs du pilotage prévision et réappro (27/10) : les deux leviers de l'opérateur, à la
 * maille référence (ou catégorie racine) × boutiques (ou réseau) × jours.
 * - multiplicateur de la prévision officielle, en % ;
 * - surcharge du coef de sécurité.
 *
 * Sur une cellule, le modificateur le plus récent qui la couvre gagne, seul : jamais de produit de
 * deux multiplicateurs. Point de vérité des bornes appliquées par le schéma Vine du backend et par
 * les curseurs du back-office. Sans dépendance runtime : exporté par le barrel.
 */

import type { ReapproNeedMethod } from './index.js'

export const FORECAST_MODIFIER_TYPES = ['multiplicateur', 'coef'] as const
export type ForecastModifierType = (typeof FORECAST_MODIFIER_TYPES)[number]

export const FORECAST_MODIFIER_DURATIONS = ['prochain_appel', 'jusqu_a_nouvel_ordre'] as const
export type ForecastModifierDuration = (typeof FORECAST_MODIFIER_DURATIONS)[number]

export const FORECAST_MODIFIER_STATUSES = ['actif', 'ecrase', 'annule', 'expire'] as const
export type ForecastModifierStatus = (typeof FORECAST_MODIFIER_STATUSES)[number]

export const FORECAST_MODIFIER_BOUNDS = {
  /** % appliqué à la prévision du moteur : −100 la met à zéro, 0 la laisse telle quelle. */
  multiplicateur: { min: -100, max: 100, decimals: 1 },
  /** Coef de sécurité, bornes de la fiche produit. */
  coef: { min: 0.5, max: 5, decimals: 2 },
} as const satisfies Record<ForecastModifierType, { min: number; max: number; decimals: number }>

/** Modificateurs créés ou annulés en une requête au plus (curseurs sur des lignes). */
export const FORECAST_MODIFIER_BATCH_MAX = 200

/**
 * Au-delà, une application des multiplicateurs encore en file (depuis sa création) ou en cours
 * (depuis son départ) est déclarée interrompue par le serveur : le regroupement (30 s) et l'attente
 * du verrou des écritures du moteur (20 min) tiennent dedans. Le back-office suit une application
 * jusqu'à ce délai, pas au-delà.
 */
export const FORECAST_MODIFIER_APPLICATION_STALE_MS = 45 * 60 * 1000
export const FORECAST_MODIFIER_REASON_MAX_LENGTH = 500

/**
 * Corrections manuelles (fenêtre « Corrections manuelles » du pilotage, `contrats/R-MOD.md`) :
 * - périmètre d'une correction : une boutique, une sélection de boutiques, ou le réseau ;
 * - évènements de l'historique des ajustements : créée, écrasée par un modificateur plus large,
 *   annulée, expirée (jour de fin passé), lue par son appel (`prochain_appel`), application des
 *   multiplicateurs à la prévision officielle, alerte acceptée (« OK »).
 */
export const FORECAST_CORRECTION_SCOPES = ['boutique', 'selection', 'reseau'] as const
export type ForecastCorrectionScope = (typeof FORECAST_CORRECTION_SCOPES)[number]

export const FORECAST_CORRECTION_EVENTS = [
  'cree',
  'ecrase',
  'annule',
  'expire',
  'appel',
  'application',
  'accepte',
] as const
export type ForecastCorrectionEvent = (typeof FORECAST_CORRECTION_EVENTS)[number]

/** Filtre « Posée sur » des corrections actives : le réseau, ou une boutique ou une sélection. */
export const FORECAST_CORRECTION_LEVELS = ['reseau', 'boutiques'] as const
export type ForecastCorrectionLevel = (typeof FORECAST_CORRECTION_LEVELS)[number]

/** Lignes par page des corrections actives et de l'historique, au plus. */
export const FORECAST_CORRECTIONS_PAGE_MAX = 100
/** Fenêtre de l'historique des ajustements : 30 jours par défaut, 92 au plus. */
export const FORECAST_CORRECTIONS_HISTORY_DEFAULT_DAYS = 30
export const FORECAST_CORRECTIONS_HISTORY_MAX_DAYS = 92

/**
 * Alertes du pilotage que l'opérateur peut accepter (« OK ») sans rien modifier. `surproduction`
 * n'est plus émise (le coef se règle par les stratégies de risque, `SPEC-STRATEGIES.md` § 13) : le
 * type reste pour les acceptations déjà enregistrées et leurs libellés dans l'historique.
 */
export const FORECAST_PILOTAGE_ALERTS = ['ecart_s1', 'surproduction'] as const
export type ForecastPilotageAlert = (typeof FORECAST_PILOTAGE_ALERTS)[number]

/** Levier → l'alerte acceptée qui le concerne (filtre par levier de l'historique). */
export const FORECAST_PILOTAGE_ALERT_OF_TYPE: Record<ForecastModifierType, ForecastPilotageAlert> =
  {
    multiplicateur: 'ecart_s1',
    coef: 'surproduction',
  }

/**
 * Source du coef historique d'une cellule en méthode prévision (`SPEC-STRATEGIES.md` § 5.1) : la
 * surcharge du pilotage, sinon le coef traditionnel effectif d'une catégorie de réappro mutualisée,
 * sinon `products.coef_secu`, sinon 1.
 */
export type HistoricSafetyCoefSource = 'surcharge' | 'mutualise' | 'produit' | 'defaut'

/**
 * Source du coef de sécurité appliqué d'une cellule, en méthode prévision : une source historique
 * (mode `shadow`), le coef automatique des stratégies (`strategie`, mode `active`), ou le coef
 * historique pris en repli quand le coef automatique n'a pas pu être calculé (`repli`).
 */
export type SafetyCoefSource = HistoricSafetyCoefSource | 'strategie' | 'repli'

/**
 * Méthode du réappro dans le pilotage, le même vocabulaire dans les vues Prévision et Réappro :
 * - `prevision` : le réappro commande sur la prévision officielle et le coef effectif ;
 * - `traditionnel` : algorithme de la catégorie de réappro, sans la prévision ni ce coef ;
 * - `hors_reappro` : ligne d'un produit sans catégorie de réappro (vue Prévision).
 */
export type PilotageMethode = 'prevision' | 'traditionnel' | 'hors_reappro'

/** Méthode d'une boutique : jamais `hors_reappro`, qui ne vaut que pour une ligne. */
export type PilotageStoreMethode = Exclude<PilotageMethode, 'hors_reappro'>

/** Méthode d'une boutique (`stores.reappro_need_method`), dans le vocabulaire du pilotage. */
export const storePilotageMethode = (needMethod: ReapproNeedMethod): PilotageStoreMethode =>
  needMethod === 'forecast' ? 'prevision' : 'traditionnel'

/**
 * Seule règle « en méthode prévision » des deux vues et du serveur : le coef effectif et le
 * multiplicateur n'y jouent qu'en `prevision` (`contrats/L1.md` § 3), donc l'alerte de surproduction
 * et le rejeu aux curseurs aussi. Hors de là (`traditionnel`, `hors_reappro`) ou sans méthode connue
 * (`null`) : pas d'alerte, on ne sait pas ou le coef n'est pas appliqué.
 */
export const isMethodePrevision = (methode: PilotageMethode | null | undefined): boolean =>
  methode === 'prevision'
